import algoliasearch, { SearchClient, SearchIndex } from 'algoliasearch/lite';

import { GF_ALGOLIA_APP_ID, gfAlgoliaSearchKey } from '@util/algoliaSearchKey';
import { shouldExcludeCurrentUserFromAnalytics } from '@util/searchAnalyticsExclusion';
import type { ProductDocument } from '@util/types/firestore/products';
import type { MasterDocument } from '../master';
import type { CategoryDocument } from '../categories';
import type { UserDocument } from '@util/types/firestore/users';

type AnyType = ProductDocument | MasterDocument | CategoryDocument | UserDocument;

export type AlgoliaSearchArgs = {
  query?: string;
  filters?: (string | string[])[];
  filterString?: string;
  page?: number;
  hitsPerPage?: number;
};

const CFG = {
  GF: {
    appId: GF_ALGOLIA_APP_ID,
    apiKey: gfAlgoliaSearchKey()
  },
  GM: {
    appId: "JW2TPM7CTX",
    apiKey: process.env.NEXT_PUBLIC_GEARMASTER_ALGOLIA_SEARCH_KEY ?? ""
  },
} as const;

type AppKey = keyof typeof CFG;

const clients: Partial<Record<AppKey, SearchClient>> = {};

function getClient(app: AppKey): SearchClient {
  if (!clients[app]) {
    const { appId, apiKey } = CFG[app];
    if (!appId || !apiKey) {
      throw new Error(`Algolia config missing for ${app}. Check env vars.`);
    }
    clients[app] = algoliasearch(appId, apiKey);
  }
  return clients[app]!;
}

function normalizeFacetFilters(filters?: (string | string[])[]) {
  if (!filters || filters.length === 0) return undefined;
  return filters as any;
}

export async function searchAlgolia(
  app: AppKey,
  indexName: string,
  args: AlgoliaSearchArgs = {}
): Promise<{ data: AnyType[]; total: number; error?: boolean }> {
  try {
    const { query = '', filters = [], filterString, page = 0, hitsPerPage = 10 } = args;
    const index: SearchIndex = getClient(app).initIndex(indexName);

    const { hits, nbHits } = await index.search<AnyType>(query, {
      hitsPerPage,
      page,
      facets: ['*'],
      facetFilters: normalizeFacetFilters(filters),
      ...(filterString ? { filters: filterString } : {}),
      sortFacetValuesBy: 'alpha',
      highlightPreTag: '<mark>',
      highlightPostTag: '</mark>',
      // Staff/house-store searches never count toward Algolia analytics
      // (GB #1392). This module can run server-side; the util returns
      // false there (analytics stays on). Fails open on any error.
      ...(shouldExcludeCurrentUserFromAnalytics() && { analytics: false }),
    });

    return { data: hits, total: nbHits };
  } catch (err) {
    console.error(`[Algolia ${app}] search error:`, err);
    // `error: true` lets callers tell a genuine failure apart from an empty
    // result set (they otherwise render identically as "no results").
    return { data: [], total: 0, error: true };
  }
}

/**
 * For a top-level category, return its total live-listing count, a per-sub-category
 * count for each provided sub, and a sample of listings — in ONE batched request.
 * (The `categories` attribute is filterOnly in the index, so per-sub counts come
 * from hitsPerPage:0 count queries rather than a facet.) The GF `categories` field
 * stores category NAMES, so callers pass names, keyed back by a stable `key`.
 * Powers the CategoryNav mega menu (sub-category counts + a featured listing).
 */
export async function getCategoryMega(
  indexName: string,
  categoryName: string,
  subs: { key: string; name: string }[],
  sampleSize = 50
): Promise<{
  total: number;
  counts: Record<string, number>;
  brands: { name: string; count: number }[];
  sample: ProductDocument[];
}> {
  try {
    const client = getClient('GF');
    const nb = (r: unknown) => (r as { nbHits?: number }).nbHits ?? 0;
    // Match the browsable search/category pages so counts equal what a click
    // actually returns (only active, verified, in-stock listings).
    const ACTIVE =
      'is_draft:false AND out_of_stock:false AND is_expired:false AND is_verified:true';

    // Round 1: total + display sample + per-sub-category counts + a wide,
    // brand-only sample for candidate selection.
    //
    // The display sample (index 0) stays small (sampleSize, full attributes)
    // — it feeds the deals/featured card below and a bigger pull there would
    // bloat every hover's payload for no benefit. Brand candidates need a
    // much wider view of the category or a low-volume brand never gets
    // picked at all: a 50-item unsorted sample missed Canon/Nikon entirely on
    // "Digital Cameras" (Zeiss and Voigtlander happened to cluster in that
    // batch instead) even though they dominate the category's true counts.
    // hitsPerPage:1000 with attributesToRetrieve:['brand'] keeps this cheap —
    // it's a name-only payload, not 1000 full product records.
    const BRAND_SAMPLE_SIZE = 1000;
    const q1 = [
      {
        indexName,
        query: '',
        params: {
          facetFilters: [[`categories:${categoryName}`]],
          filters: ACTIVE,
          hitsPerPage: sampleSize,
          analytics: false,
        },
      },
      {
        indexName,
        query: '',
        params: {
          facetFilters: [[`categories:${categoryName}`]],
          filters: ACTIVE,
          hitsPerPage: BRAND_SAMPLE_SIZE,
          attributesToRetrieve: ['brand'],
          analytics: false,
        },
      },
      ...subs.map((s) => ({
        indexName,
        query: '',
        params: {
          facetFilters: [[`categories:${s.name}`]],
          filters: ACTIVE,
          hitsPerPage: 0,
          analytics: false,
        },
      })),
    ];
    const r1 = await client.search<ProductDocument>(q1);
    const first = r1.results[0] as unknown as { nbHits: number; hits: ProductDocument[] };
    const brandSample = r1.results[1] as unknown as { hits: Pick<ProductDocument, 'brand'>[] };
    const total = first.nbHits ?? 0;
    const sample = first.hits ?? [];
    const counts: Record<string, number> = {};
    subs.forEach((s, i) => {
      counts[s.key] = nb(r1.results[i + 2]);
    });

    // Candidate brands from the wide brand-only sample (brand is filterOnly,
    // so it can't be faceted directly), then their true count within this
    // category via count queries.
    const freq: Record<string, number> = {};
    for (const h of brandSample.hits ?? []) {
      const b = h.brand?.trim();
      if (b) freq[b] = (freq[b] ?? 0) + 1;
    }
    const candidates = Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([b]) => b);

    let brands: { name: string; count: number }[] = [];
    if (candidates.length) {
      const q2 = candidates.map((b) => ({
        indexName,
        query: '',
        params: {
          facetFilters: [[`categories:${categoryName}`], [`brand:${b}`]],
          filters: ACTIVE,
          hitsPerPage: 0,
          analytics: false,
        },
      }));
      const r2 = await client.search<ProductDocument>(q2);
      brands = candidates
        .map((name, i) => ({ name, count: nb(r2.results[i]) }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);
    }

    return { total, counts, brands, sample };
  } catch (err) {
    console.error('[Algolia GF] getCategoryMega error:', err);
    return { total: 0, counts: {}, brands: [], sample: [] };
  }
}

export const searchGearFocusAlgolia = (
  indexName: string,
  args?: AlgoliaSearchArgs
) => searchAlgolia('GF', indexName, args);

export const searchGearMasterAlgolia = (
  indexName: string,
  args?: AlgoliaSearchArgs
) => searchAlgolia('GM', indexName, args);

export async function searchBothApps(
  indexNameGF: string,
  indexNameGM: string,
  args: AlgoliaSearchArgs = {}
): Promise<{ data: (AnyType & { __source: 'GF' | 'GM' })[]; total: number }> {
  const [gf, gm] = await Promise.all([
    searchAlgolia('GF', indexNameGF, args),
    searchAlgolia('GM', indexNameGM, args),
  ]);

  const data = [
    ...gf.data.map((d) => ({ ...d, __source: 'GF' as const })),
    ...gm.data.map((d) => ({ ...d, __source: 'GM' as const })),
  ];

  return { data, total: gf.total + gm.total };
}
